import * as React from 'react';

import {
  enumUnit,
  enumWorkOrderStatus,
  Unit,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  Card,
  Col,
  DataGrid,
  LabeledInfo,
  uuid,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucidePencil, LucidePlus } from 'lucide-react';

import { updateWorkOrderAtom } from '../../_graphql/mutationWorkOrder';
import * as state from '../../_state/workOrderDetail.state';
import { useTranslations } from '../../i18n/useTranslations';
import {
  WorkOrderInputItemRecord,
  WorkOrderOutputItemRecord,
} from '../../types/workOrder';

import { useWorkOrderInputItemsColumns } from './hooks/useWorkOrderInputItemsColumns';
import { useWorkOrderOutputItemsColumns } from './hooks/useWorkOrderOutputItemsColumns';
import {
  WorkOrderInputItemRow,
  WorkOrderInputItemData,
} from './WorkOrderInputItemRow';
import {
  WorkOrderOutputItemRow,
  WorkOrderOutputItemData,
} from './WorkOrderOutputItemRow';

import { formatZonedDateTime } from '~/shared/utils/dateTime';

//
// ⚛️ ⚛️ ⚛️ WORK ORDER DETAIL TAB CONTENT ⚛️ ⚛️ ⚛️
//
export const WorkOrderDetailTabContent = () => {
  const t = useTranslations();

  // States
  const [isEditingItems, setIsEditingItems] = React.useState(false);
  const [isSavingItems, setIsSavingItems] = React.useState(false);
  const [editingInputItems, setEditingInputItems] = React.useState<
    WorkOrderInputItemData[]
  >([]);
  const [editingOutputItems, setEditingOutputItems] = React.useState<
    WorkOrderOutputItemData[]
  >([]);

  // Atoms
  const { data: workOrder, refetch } = useAtomValue(state.$remoteWorkOrder);
  const updateWorkOrder = useSetAtom(updateWorkOrderAtom({}));

  // Other data hooks
  const toast = useToast();
  const inputItemsColumns = useWorkOrderInputItemsColumns();
  const outputItemsColumns = useWorkOrderOutputItemsColumns();

  const canEditItems =
    workOrder?.status === enumWorkOrderStatus.Draft ||
    workOrder?.status === enumWorkOrderStatus.Open;

  // Callbacks
  const handleEnterEditMode = React.useCallback(() => {
    if (!workOrder) return;

    setEditingInputItems(
      workOrder.inputItems.map((item) => ({
        id: `existing-${item.id}`,
        existingId: item.id,
        itemId: item.itemId,
        lotId: item.lotId,
        tagId: item.tagId,
        plannedQuantityUnit: item.plannedQuantityUnit as Unit,
        plannedQuantity: item.plannedQuantityValue.toString(),
        notes: item.notes,
      })),
    );

    setEditingOutputItems(
      workOrder.outputItems.map((item) => ({
        id: `existing-${item.id}`,
        existingId: item.id,
        itemId: item.itemId,
        targetLotId: item.targetLotId,
        targetTagId: item.targetTagId,
        expectedQuantityUnit: item.expectedQuantityUnit as Unit,
        expectedQuantity: item.expectedQuantityValue.toString(),
        notes: item.notes,
      })),
    );

    setIsEditingItems(true);
  }, [workOrder]);

  const handleCancelEditMode = React.useCallback(() => {
    setIsEditingItems(false);
    setEditingInputItems([]);
    setEditingOutputItems([]);
  }, []);

  const handleSaveItems = React.useCallback(async () => {
    if (!workOrder) return;

    try {
      setIsSavingItems(true);
      await updateWorkOrder({
        id: workOrder.id,
        inputItems: editingInputItems.map((item) => ({
          id: item.existingId ?? null,
          itemId: item.itemId,
          lotId: item.lotId ?? null,
          tagId: item.tagId ?? null,
          plannedQuantityUnit: item.plannedQuantityUnit,
          plannedQuantity: parseFloat(item.plannedQuantity) || 0,
          notes: item.notes ?? null,
        })),
        outputItems: editingOutputItems.map((item) => ({
          id: item.existingId ?? null,
          itemId: item.itemId,
          targetLotId: item.targetLotId ?? null,
          targetTagId: item.targetTagId ?? null,
          expectedQuantityUnit: item.expectedQuantityUnit,
          expectedQuantity: parseFloat(item.expectedQuantity) || 0,
          notes: item.notes ?? null,
        })),
      });
      toast.success(t.workOrderDetailView.itemsEdit.saveSuccess);
      setIsEditingItems(false);
      setEditingInputItems([]);
      setEditingOutputItems([]);
      refetch();
    } catch (err) {
      toast.error(
        t.workOrderDetailView.itemsEdit.saveError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    } finally {
      setIsSavingItems(false);
    }
  }, [
    workOrder,
    editingInputItems,
    editingOutputItems,
    updateWorkOrder,
    toast,
    refetch,
    t,
  ]);

  const handleAddInputItem = React.useCallback(() => {
    setEditingInputItems((prev) => [
      ...prev,
      {
        id: `new-${uuid()}`,
        itemId: '',
        plannedQuantityUnit: enumUnit.Pounds,
        plannedQuantity: '',
      },
    ]);
  }, []);

  const handleUpdateInputItem = React.useCallback(
    (id: string, data: Partial<WorkOrderInputItemData>) => {
      setEditingInputItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, ...data } : item)),
      );
    },
    [],
  );

  const handleRemoveInputItem = React.useCallback((id: string) => {
    setEditingInputItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const handleAddOutputItem = React.useCallback(() => {
    setEditingOutputItems((prev) => [
      ...prev,
      {
        id: `new-${uuid()}`,
        itemId: '',
        expectedQuantityUnit: enumUnit.Pounds,
        expectedQuantity: '',
      },
    ]);
  }, []);

  const handleUpdateOutputItem = React.useCallback(
    (id: string, data: Partial<WorkOrderOutputItemData>) => {
      setEditingOutputItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, ...data } : item)),
      );
    },
    [],
  );

  const handleRemoveOutputItem = React.useCallback((id: string) => {
    setEditingOutputItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  return !workOrder ? null : (
    <div className="@WorkOrderDetailTabContent space-y-6 p-4">
      <Card className="p-4" title={t.workOrderDetailView.tabs.overview}>
        <div className="grid grid-cols-3 gap-4">
          <LabeledInfo
            label={t.workOrderDetailView.overview.processingType}
            value={workOrder.processingTypeName}
          />
          <LabeledInfo
            label={t.workOrderDetailView.overview.facility}
            value={workOrder.facilityName}
          />
          <LabeledInfo
            label={t.workOrderDetailView.overview.createdBy}
            value={workOrder.createdByName}
          />
          <LabeledInfo
            label={t.workOrderDetailView.overview.scheduledStart}
            value={formatZonedDateTime(workOrder.scheduledStartDate, {
              includeTime: true,
            })}
          />
          <LabeledInfo
            label={t.workOrderDetailView.overview.scheduledEnd}
            value={formatZonedDateTime(workOrder.scheduledEndDate, {
              includeTime: true,
            })}
          />
          <LabeledInfo
            label={t.workOrderDetailView.overview.isTolling}
            value={workOrder.tollingType !== 'None' ? 'Yes' : 'No'}
          />
          {workOrder.actualStartDate && (
            <LabeledInfo
              label={t.workOrderDetailView.overview.actualStart}
              value={formatZonedDateTime(workOrder.actualStartDate, {
                includeTime: true,
              })}
            />
          )}
          {workOrder.actualEndDate && (
            <LabeledInfo
              label={t.workOrderDetailView.overview.actualEnd}
              value={formatZonedDateTime(workOrder.actualEndDate, {
                includeTime: true,
              })}
            />
          )}
          {workOrder.notes && (
            <div className="col-span-3">
              <LabeledInfo
                label={t.workOrderDetailView.overview.notes}
                value={workOrder.notes}
              />
            </div>
          )}
        </div>
      </Card>

      {isEditingItems ? (
        <>
          <Card
            className="gap-4 p-4"
            title={t.workOrderDetailView.inputItems.title}
          >
            <div className="flex items-center justify-between">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  color="neutral"
                  onPress={handleCancelEditMode}
                  isDisabled={isSavingItems}
                >
                  {t.workOrderDetailView.itemsEdit.cancel}
                </Button>
                <Button
                  color="primary"
                  onPress={handleSaveItems}
                  isDisabled={isSavingItems}
                >
                  {isSavingItems
                    ? t.workOrderDetailView.itemsEdit.saving
                    : t.workOrderDetailView.itemsEdit.save}
                </Button>
              </div>
            </div>
            <Col gap2>
              {editingInputItems.map((item) => (
                <WorkOrderInputItemRow
                  key={item.id}
                  item={item}
                  onUpdate={handleUpdateInputItem}
                  onRemove={handleRemoveInputItem}
                  isDisabled={isSavingItems}
                />
              ))}
              {editingInputItems.length === 0 && (
                <div className="py-4 text-center text-neutral/60">
                  <p>{t.workOrdersView.dialog.inputItems.empty}</p>
                </div>
              )}
              <Button
                variant="outline"
                color="neutral"
                icon={<LucidePlus size={16} />}
                onPress={handleAddInputItem}
                isDisabled={isSavingItems}
              >
                {t.workOrderDetailView.itemsEdit.addInput}
              </Button>
            </Col>
          </Card>

          <Card title={t.workOrderDetailView.outputItems.title}>
            <Col gap2>
              {editingOutputItems.map((item) => (
                <WorkOrderOutputItemRow
                  key={item.id}
                  item={item}
                  onUpdate={handleUpdateOutputItem}
                  onRemove={handleRemoveOutputItem}
                  isDisabled={isSavingItems}
                />
              ))}
              {editingOutputItems.length === 0 && (
                <div className="py-4 text-center text-neutral/60">
                  <p>{t.workOrdersView.dialog.outputItems.empty}</p>
                </div>
              )}
              <Button
                variant="outline"
                color="neutral"
                icon={<LucidePlus size={16} />}
                onPress={handleAddOutputItem}
                isDisabled={isSavingItems}
              >
                {t.workOrderDetailView.itemsEdit.addOutput}
              </Button>
            </Col>
          </Card>
        </>
      ) : (
        <>
          <Card title={t.workOrderDetailView.inputItems.title}>
            {canEditItems && (
              <Button
                variant="outline"
                color="neutral"
                icon={<LucidePencil size={16} />}
                onPress={handleEnterEditMode}
              >
                {t.workOrderDetailView.itemsEdit.editButton}
              </Button>
            )}
            {workOrder.inputItems.length > 0 ? (
              <DataGrid<WorkOrderInputItemRecord>
                columns={inputItemsColumns}
                dataSource={workOrder.inputItems}
                rowKey="id"
                aria-label={t.workOrderDetailView.inputItems.table.aria}
                className="rounded-sm text-sm"
                fullWidth
                isContained
              />
            ) : (
              <div className="py-8 text-center text-neutral/60">
                <p className="font-medium">
                  {t.workOrderDetailView.inputItems.emptyState.title}
                </p>
                <p className="text-sm">
                  {t.workOrderDetailView.inputItems.emptyState.subtitle}
                </p>
              </div>
            )}
          </Card>

          <Card title={t.workOrderDetailView.outputItems.title}>
            {workOrder.outputItems.length > 0 ? (
              <DataGrid<WorkOrderOutputItemRecord>
                columns={outputItemsColumns}
                dataSource={workOrder.outputItems}
                rowKey="id"
                isContained
                aria-label={t.workOrderDetailView.outputItems.table.aria}
                className="w-full rounded-sm text-sm"
                fullWidth
              />
            ) : (
              <div className="py-8 text-center text-neutral/60">
                <p className="font-medium">
                  {t.workOrderDetailView.outputItems.emptyState.title}
                </p>
                <p className="text-sm">
                  {t.workOrderDetailView.outputItems.emptyState.subtitle}
                </p>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
};
